import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { X, Megaphone } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import type { Settings } from "@shared/schema";

const SETTING_KEY = "site_announcement";
const USER_DISMISS_KEY = "site_announcement_dismissed";
const LOCAL_DISMISS_PREFIX = "site_announcement_dismissed_v";

interface AnnouncementValue {
  enabled: boolean;
  message: string;
  ctaLabel?: string;
  ctaUrl?: string;
  /**
   * Bumping the version invalidates prior dismissals so users see the new banner.
   * Stored as an opaque string (e.g. an ISO date or semver) so the admin UI can
   * use any version scheme they like.
   */
  version: string;
}

function parseAnnouncement(raw: unknown): AnnouncementValue | null {
  if (raw === null || raw === undefined) return null;

  // The setting can come back as either a JSON-encoded string or an already
  // decoded object (Postgres jsonb). Normalize to a record before validating.
  let parsed: unknown = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const obj = parsed as Record<string, unknown>;

  // Strict boolean parsing to handle legacy non-boolean stored values
  // (e.g. "true"/"false" strings or 0/1 numbers from older admin writes).
  const rawEnabled = obj.enabled;
  let enabled = false;
  if (typeof rawEnabled === "boolean") {
    enabled = rawEnabled;
  } else if (typeof rawEnabled === "string") {
    enabled = rawEnabled.trim().toLowerCase() === "true";
  } else if (typeof rawEnabled === "number") {
    enabled = rawEnabled === 1;
  }
  if (!enabled) return null;

  if (typeof obj.message !== "string" || obj.message.trim().length === 0) {
    return null;
  }

  // Normalize version to a string so the admin UI can store ISO dates, semver,
  // or numeric versions interchangeably.
  let version = "1";
  if (typeof obj.version === "string" && obj.version.trim().length > 0) {
    version = obj.version.trim();
  } else if (typeof obj.version === "number") {
    version = String(obj.version);
  }

  return {
    enabled: true,
    message: obj.message,
    ctaLabel: typeof obj.ctaLabel === "string" ? obj.ctaLabel : undefined,
    ctaUrl: typeof obj.ctaUrl === "string" ? obj.ctaUrl : undefined,
    version,
  };
}

export function SiteAnnouncementBanner() {
  const { user, isAuthenticated } = useAuth();
  const [localDismissed, setLocalDismissed] = useState(false);

  const { data: setting } = useQuery<Settings | null>({
    queryKey: ["/api/settings", SETTING_KEY],
    queryFn: async () => {
      const res = await fetch(`/api/settings/${SETTING_KEY}`, { credentials: "include" });
      if (!res.ok) {
        // 404 simply means no announcement is configured
        return null;
      }
      return (await res.json()) as Settings;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const announcement = useMemo(() => parseAnnouncement(setting?.value), [setting]);
  const version = announcement?.version ?? "1";
  const localKey = `${LOCAL_DISMISS_PREFIX}${version}`;

  // For signed-in users, fetch their per-user dismissal flag
  const userDismissalEnabled = isAuthenticated && !!announcement;
  const { data: userDismissal, isLoading: userDismissalLoading } = useQuery<
    { value: unknown } | null
  >({
    queryKey: ["/api/user-settings", USER_DISMISS_KEY],
    queryFn: async () => {
      const res = await fetch(`/api/user-settings`, { credentials: "include" });
      if (!res.ok) return null;
      const list = (await res.json()) as Array<{ key: string; value: unknown }>;
      const found = list.find((s) => s.key === USER_DISMISS_KEY);
      return found ? { value: found.value } : null;
    },
    enabled: userDismissalEnabled,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!announcement) return;
    try {
      const stored = window.localStorage.getItem(localKey);
      if (stored === "1") {
        setLocalDismissed(true);
      } else {
        setLocalDismissed(false);
      }
    } catch {
      // ignore localStorage errors
    }
  }, [announcement, localKey]);

  const dismissMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) return null;
      return apiRequest("PUT", `/api/user-settings/${encodeURIComponent(USER_DISMISS_KEY)}`, {
        value: { version },
      });
    },
  });

  if (!announcement) return null;

  // Avoid flashing the banner for signed-in users while their dismissal
  // preference is still loading from the server.
  if (userDismissalEnabled && userDismissalLoading) return null;

  // Compare per-user dismissal version against the current banner version.
  // Versions are opaque strings — only an exact match counts as "already dismissed",
  // so any change to the version (e.g. bumping the date) re-shows the banner.
  const userDismissedVersion = (() => {
    const v = userDismissal?.value;
    if (typeof v === "object" && v !== null && "version" in v) {
      const raw = (v as { version?: unknown }).version;
      if (typeof raw === "string") return raw;
      if (typeof raw === "number") return String(raw);
    }
    return null;
  })();
  const userHasDismissed =
    isAuthenticated && userDismissedVersion !== null && userDismissedVersion === version;

  if (userHasDismissed || localDismissed) return null;

  const handleDismiss = () => {
    setLocalDismissed(true);
    try {
      window.localStorage.setItem(localKey, "1");
    } catch {
      // ignore
    }
    if (isAuthenticated) {
      dismissMutation.mutate();
    }
  };

  return (
    <div
      className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-100"
      data-testid="banner-site-announcement"
      role="region"
      aria-label="Site announcement"
    >
      <div className="container-responsive py-2 flex items-center gap-3">
        <Megaphone className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="flex-1 text-xs sm:text-sm leading-snug">
          <span data-testid="text-announcement-message">{announcement.message}</span>
          {announcement.ctaUrl && (
            <>
              {' '}
              {announcement.ctaUrl.startsWith('/') ? (
                <Link
                  href={announcement.ctaUrl}
                  className="font-semibold underline underline-offset-2 hover:no-underline"
                  data-testid="link-announcement-cta"
                >
                  {announcement.ctaLabel || 'Learn more'}
                </Link>
              ) : (
                <a
                  href={announcement.ctaUrl}
                  className="font-semibold underline underline-offset-2 hover:no-underline"
                  data-testid="link-announcement-cta"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {announcement.ctaLabel || 'Learn more'}
                </a>
              )}
            </>
          )}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
          aria-label="Dismiss announcement"
          data-testid="button-dismiss-announcement"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
